import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { InputParam } from "../../../types/grid.ts";
import type { ServerSetting } from "../../../types/session.ts";
import type { AdvancedFilterProps } from "./types.ts";
import {
  getBrowserTimeZone, getClientTimeZone,
  getTimeZoneOffsetMinutes, getWallClockInZone,
} from "../../../util/clientEnv.ts";
import styles from "./dateFilter.module.scss";

// --- Default-value logic (matching WebAdmin2 DateFilterDefaultToTypes) ---

enum DefaultMode {
  Empty,
  Setting,
  SubtractMonth,
  Now,
  EndLastMonth,
  StartLastMonth,
  NowToEmpty,
  EmptyToNow,
}

interface DateSettings {
  defaultMode: DefaultMode;
  explicitEmpty: boolean;
  includeOffset: boolean;
  /** Date column with no intrinsic instant — picker shows date only, output
   *  is "YYYY-MM-DD" with no time or offset. Suppresses tz/offset handling. */
  dateOnly: boolean;
}

function parseFilterSettings(raw: string | undefined): DateSettings {
  const result: DateSettings = {
    defaultMode: DefaultMode.Empty,
    explicitEmpty: false,
    includeOffset: false,
    dateOnly: false,
  };
  if (!raw) return result;
  for (const part of raw.split("|")) {
    switch (part.trim().toUpperCase()) {
      case "DEFAULTTOEMPTY":
        result.defaultMode = DefaultMode.Empty;
        result.explicitEmpty = true;
        break;
      case "DEFAULTTOSETTING":
        result.defaultMode = DefaultMode.Setting;
        break;
      case "DEFAULTTOMONTHLY":
        result.defaultMode = DefaultMode.SubtractMonth;
        break;
      case "DEFAULTTONOW":
        result.defaultMode = DefaultMode.Now;
        break;
      case "EMPTYTONOW":
        result.defaultMode = DefaultMode.EmptyToNow;
        result.explicitEmpty = true;
        break;
      case "NOWTOEMPTY":
        result.defaultMode = DefaultMode.NowToEmpty;
        result.explicitEmpty = true;
        break;
      case "INCLUDEOFFSET":
        result.includeOffset = true;
        break;
      case "DATEONLY":
        result.dateOnly = true;
        break;
    }
  }
  return result;
}

function getServerSettingInt(
  serverSettings: ServerSetting[],
  key: string,
  fallback: number,
): number {
  for (const s of serverSettings) {
    if (s.Key === key) {
      const v = Number(s.Value);
      return isNaN(v) ? fallback : v;
    }
  }
  return fallback;
}

/** Format a Date to "YYYY-MM-DD HH:mm" (or "YYYY-MM-DD" when dateOnly).
 *  Honours active SA Client tz override — wall clock + offset come from the
 *  perceived zone, not the browser. No-override path is identical to the
 *  legacy browser-local code. dateOnly suppresses time + offset; date
 *  columns have no instant so neither tz nor offset apply.              */
function formatDate(d: Date, includeOffset: boolean, dateOnly = false): string {
  const zone = getClientTimeZone();
  const overridden = !dateOnly && zone !== getBrowserTimeZone();
  let yyyy: number, MM: number, dd: number, HH: number, mm: number, off: number;
  if (overridden) {
    const w = getWallClockInZone(zone, d);
    yyyy = w.year; MM = w.month; dd = w.day; HH = w.hour; mm = w.minute;
    off = getTimeZoneOffsetMinutes(zone, d);
  } else {
    yyyy = d.getFullYear(); MM = d.getMonth() + 1; dd = d.getDate();
    HH = d.getHours(); mm = d.getMinutes();
    off = -d.getTimezoneOffset();
  }
  if (dateOnly) {
    return `${yyyy}-${String(MM).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  }
  let s = `${yyyy}-${String(MM).padStart(2, "0")}-${String(dd).padStart(2, "0")} ${String(HH).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  if (includeOffset) {
    const sign = off >= 0 ? "+" : "-";
    const absOff = Math.abs(off);
    s += ` ${sign}${String(Math.floor(absOff / 60)).padStart(2, "0")}:${String(absOff % 60).padStart(2, "0")}`;
  }
  return s;
}

/** Convert backend format to input value. dateOnly → "YYYY-MM-DD" for
 *  <input type="date">; otherwise "YYYY-MM-DDTHH:mm" for datetime-local. */
function toInputValue(backendStr: string, dateOnly = false): string {
  if (!backendStr) return "";
  if (dateOnly) return backendStr.slice(0, 10);
  // Strip offset suffix if present
  const core = backendStr.replace(/\s+[+-]\d{2}:\d{2}$/, "").trim();
  // Replace space with T for datetime-local
  return core.replace(" ", "T");
}

/** Convert input value to backend format. dateOnly → emit literal
 *  "YYYY-MM-DD" with no time, no offset, no zone math (date columns have
 *  no instant). Override path otherwise: user picked numbers thinking
 *  they were in the override zone, so trust the literal wall clock and
 *  append the override offset for that wall instant (two-pass for DST). */
function fromInputValue(inputVal: string, includeOffset: boolean, dateOnly = false): string {
  if (!inputVal) return "";
  if (dateOnly) {
    const d = inputVal.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "";
  }
  const zone = getClientTimeZone();
  if (zone === getBrowserTimeZone()) {
    const d = new Date(inputVal);
    if (isNaN(d.getTime())) return "";
    return formatDate(d, includeOffset);
  }
  const datePart = inputVal.slice(0, 10);
  const timePart = inputVal.length > 11 ? inputVal.slice(11, 16) : "00:00";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return "";
  let s = `${datePart} ${timePart}`;
  if (includeOffset) {
    const [yy, MM, dd] = datePart.split("-").map(Number);
    const [HH, mi] = timePart.split(":").map(Number);
    // Treat wall as UTC then refine: ask zone offset at that instant, then
    // subtract to land near the true UTC instant for a second offset query.
    const approx = new Date(Date.UTC(yy!, (MM ?? 1) - 1, dd ?? 1, HH ?? 0, mi ?? 0));
    const off1 = getTimeZoneOffsetMinutes(zone, approx);
    const refined = new Date(approx.getTime() - off1 * 60000);
    const off = getTimeZoneOffsetMinutes(zone, refined);
    const sign = off >= 0 ? "+" : "-";
    const abs = Math.abs(off);
    s += ` ${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
  }
  return s;
}

interface DefaultDates {
  from: string;
  to: string;
}

function computeDefaults(
  settings: DateSettings,
  serverSettings: ServerSetting[],
  colName: string,
): DefaultDates {
  const now = new Date();

  if (settings.explicitEmpty && settings.defaultMode === DefaultMode.Empty) {
    return { from: "", to: "" };
  }

  if (settings.defaultMode === DefaultMode.NowToEmpty) {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return { from: formatDate(d, settings.includeOffset, settings.dateOnly), to: "" };
  }

  if (settings.defaultMode === DefaultMode.EmptyToNow) {
    const d = new Date(now);
    d.setHours(23, 59, 0, 0);
    return { from: "", to: formatDate(d, settings.includeOffset, settings.dateOnly) };
  }

  // Compute "from" date based on mode
  let fromDate: Date;
  switch (settings.defaultMode) {
    case DefaultMode.Now: {
      fromDate = new Date(now);
      fromDate.setHours(0, 0, 0, 0);
      break;
    }
    case DefaultMode.SubtractMonth: {
      fromDate = new Date(now);
      fromDate.setMonth(fromDate.getMonth() - 1);
      fromDate.setHours(0, 0, 0, 0);
      break;
    }
    case DefaultMode.StartLastMonth: {
      fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      break;
    }
    case DefaultMode.EndLastMonth: {
      fromDate = new Date(now.getFullYear(), now.getMonth(), 0, 0, 0, 0, 0); // day 0 = last day prev month
      break;
    }
    case DefaultMode.Setting: {
      const days = getServerSettingInt(serverSettings, "SLAdmin.DefaultSearchStartDate", 7);
      fromDate = new Date(now);
      fromDate.setDate(fromDate.getDate() - days);
      fromDate.setHours(0, 0, 0, 0);
      break;
    }
    default: {
      // Default: 7 days ago (or 30 if column name contains "month")
      const days = colName.toLowerCase().includes("month") ? 30 : 7;
      fromDate = new Date(now);
      fromDate.setDate(fromDate.getDate() - days);
      fromDate.setHours(0, 0, 0, 0);
      break;
    }
  }

  const toDate = new Date(now);
  toDate.setHours(23, 59, 0, 0);

  return {
    from: formatDate(fromDate, settings.includeOffset, settings.dateOnly),
    to: formatDate(toDate, settings.includeOffset, settings.dateOnly),
  };
}

function buildParams(colName: string, from: string, to: string, dateOnly: boolean): InputParam[] {
  const params: InputParam[] = [];
  params.push({
    sColumn: colName + "0",
    oValue: from || "",
    Type: from ? ParamType.EqualOrLargerThen : ParamType.Unused,
  });
  params.push({
    sColumn: colName + "1",
    oValue: to || "",
      Type: to ? (dateOnly ? ParamType.EqualOrLessThen : ParamType.LessThen) : ParamType.Unused,
  });
  return params;
}

export function DateFilter({ info, value, onChange, onSearch, cache }: AdvancedFilterProps) {
  const colName = info.ColumnName;

  const settings = useMemo(() => parseFilterSettings(info.FilterSettings), [info.FilterSettings]);

  const defaults = useMemo(
    () => computeDefaults(settings, cache?.ServerSettings ?? [], colName),
    [settings, cache?.ServerSettings, colName],
  );

  // If an entry exists for a side, its oValue is authoritative (even if ""),
  // which preserves explicitly-cleared state on reload. If no entry exists,
  // fall back to the computed default. `oValue === true` is the codec's
  // checkbox-default sentinel (filterUrlCodec.ts) — meaningless for a date
  // input, so we treat it the same as "no entry" and fall through to defaults.
  const [fromDate, setFromDate] = useState(() => {
    const p = value.find((v) => v.sColumn === colName + "0");
    if (p !== undefined && p.oValue !== true) return String(p.oValue ?? "");
    return defaults.from;
  });
  const [toDate, setToDate] = useState(() => {
    const p = value.find((v) => v.sColumn === colName + "1");
    if (p !== undefined && p.oValue !== true) return String(p.oValue ?? "");
    return defaults.to;
  });

  // Emit defaults on mount — skip if already loaded with external values
  const mountedWithValues = useRef(value.length > 0);
  const didEmitDefaults = useRef(false);
  useEffect(() => {
    if (didEmitDefaults.current) return;
    didEmitDefaults.current = true;
    if (mountedWithValues.current) return;
    if (defaults.from || defaults.to) {
      onChange(buildParams(colName, defaults.from, defaults.to, settings.dateOnly));
    }
  }, [defaults, colName, onChange]);

  // Reset to defaults when cleared externally
  useEffect(() => {
    if (value.length === 0 && didEmitDefaults.current) {
      setFromDate(defaults.from);
      setToDate(defaults.to);
      if (defaults.from || defaults.to) {
          onChange(buildParams(colName, defaults.from, defaults.to, settings.dateOnly));
      }
    }
  }, [value, defaults, colName, onChange]);

  const emitChange = useCallback(
    (from: string, to: string) => {
      onChange(buildParams(colName, from, to, settings.dateOnly));
    },
    [colName, onChange],
  );

  const handleFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const backend = fromInputValue(e.target.value, settings.includeOffset, settings.dateOnly);
      setFromDate(backend);
      emitChange(backend, toDate);
    },
    [emitChange, toDate, settings.includeOffset, settings.dateOnly],
  );

  const handleToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let backend = fromInputValue(e.target.value, settings.includeOffset, settings.dateOnly);
      // Snap "to" time to 23:59 if user only picked a date (time = 00:00).
      // Doesn't apply when dateOnly — there is no time component to snap.
      if (!settings.dateOnly && backend && backend.endsWith(" 00:00")) {
        backend = backend.replace(" 00:00", " 23:59");
      }
      setToDate(backend);
      emitChange(fromDate, backend);
    },
    [emitChange, fromDate, settings.includeOffset, settings.dateOnly],
  );

  const handleToday = useCallback(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 0, 0);
    const f = formatDate(start, settings.includeOffset, settings.dateOnly);
    const t = formatDate(end, settings.includeOffset, settings.dateOnly);
    setFromDate(f);
    setToDate(t);
    emitChange(f, t);
  }, [settings.includeOffset, settings.dateOnly, emitChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onSearch();
    },
    [onSearch],
  );

  const inputType = settings.dateOnly ? "date" : "datetime-local";

  return (
    <div className={styles.dateRange}>
      <input
        type={inputType}
        value={toInputValue(fromDate, settings.dateOnly)}
        onChange={handleFromChange}
        onKeyDown={handleKeyDown}
        className={styles.dateInput}
      />
      <span className={styles.separator}>to</span>
      <input
        type={inputType}
        value={toInputValue(toDate, settings.dateOnly)}
        onChange={handleToChange}
        onKeyDown={handleKeyDown}
        className={styles.dateInput}
      />
      <button type="button" className={styles.todayBtn} onClick={handleToday}>Today</button>
    </div>
  );
}
